import { useLocation, useNavigate } from "react-router-dom";
import PageHeader, {
  BreadcrumbsModel,
} from "../../../../shared/PageHeader/PageHeader";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ExportIcon from "../../../../assets/images/icons/export.svg";
import PageWrapper from "../../../../shared/PageWrapper/PageWrapper";
import {
  Box,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Button as MatButton,
  Menu,
  MenuItem,
  Typography,
  TablePagination,
  TableSortLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import Switch from "../../../../components/Switch/Switch";
import { useCallback, useEffect, useRef, useState } from "react";
import Search from "../../../../components/Search/Search";
import ViewIcons from "../../../../assets/images/icons/view.svg";
import RespondIcons from "../../../../assets/images/icons/respond.svg"
import API from "../../../../api/axios";
import DataNotFound from "../../../../shared/DataNotFound/DataNotFound";
import moment from "moment";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import QueryChangeStatusDialog from "./QueryChangeStatusDialog";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../store/slices/toastSlice";
import PermissionAction from "../../../../shared/PermissionAction/PermissionAction";
import {
  getUser,
  PERMISSIONS_SLUGS,
  ROLES_SLUGS,
} from "../../../../utils/types";
import { QUERY_STATUS} from "../../../../utils/static-masters";
import DateRangePicker from "../../../../shared/DateRangePicker/DateRangePicker"
import DifferenceIcon from '@mui/icons-material/Difference';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { handleDate } from "../../../../shared/CommonFunctions/CommonFunction";

const QueryManagementListing = () => {
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSwitchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [action, setAction] = useState("");
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchData, setSwitchData] = useState<any>({});
  const [switchLoading, setSwitchLoading] = useState<boolean>(false);
  const [switchDialogReset, setSwitchDialogReset] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState("");
  const [permissionValid, setPermissionValid] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<any>("asc");
  const [sortBy, setSortBy] = useState("");

  const persistedQueryStatus = location?.state?.queryStatusState ?? undefined;
  const persistedPagination = location?.state?.paginationState ?? {};

  const [selectedQueryStatus, setSelectedQueryStatus] = useState<any>(persistedQueryStatus);
  const [page, setPage] = useState(persistedPagination.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(persistedPagination.rowsPerPage ?? 20);
  const [offset, setOffset] = useState<number>(persistedPagination.offset ?? 0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<any>({});
  const preSearchPagination = useRef({ page: 0, offset: 0 });
  const dispatch = useDispatch();

  let data = {
    queryResponse: true,
    offset: offset,
    limit: rowsPerPage,
    search: searchValue,
    sort: sortBy,
    status:selectedQueryStatus?.id || "",
    start_date:dateRange?.startDate,
    end_date:dateRange?.endDate,
  };

  const combinedName = (firstName: string, lastName: string) => {
    if (!firstName) {
      return "NA";
    }
    return `${firstName} ${lastName}`;
  };

  const handleMenuOpen = (setAnchor: any) => (event: any) => {
    setAnchor(event.currentTarget);
  };

  const handleMenuClose = (setAnchor: any) => () => {
    setAnchor(null);
  };

  const handleDateRangeChange = (dateRange: { startDate:string | null; endDate: string | null }) => {
    setDateRange(dateRange);
    setOffset(0)
    setPage(0)
    preSearchPagination.current = { page: 0, offset: 0 };
  };

  const handleSwitchClick = () => {
    setAction("switch");
    setSwitchDialogOpen(true);
  };

  const handleSort = (property: any) => {
    const isAsc = sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(sortOrder === "asc" ? `-${property}` : property);
  };

  const handleSwitchConfirm = async (data?: any) => {
    setSwitchLoading(true);
    if (switchData) {
      try {
        let response = await API.put("/query/update/status", {
          query_id: switchData?.id,
          query_status: switchData?.query_status == "open" ? "closed" : "open",
          remarks: data?.reason || "",
        });
        if (response?.status == 200 || response?.statusText == "OK") {
          dispatch(
            setToast({
              message: response?.data?.message,
              severity: "success",
              open: true,
            })
          );
          getQuery();
          setSwitchDialogReset(true);
          setSwitchDialogOpen(false);
        }
      } catch (err) {
        // Query status update failed; dialog/loading cleanup still executes below
      } finally {
        setSwitchLoading(false);
      }
    }
  };

  const handleSwitchCancel = () => {
    setSwitchDialogOpen(false);
  };

  const getQuery = async () => {
    setLoading(true);
    try {
      const response = await API.post("/query/get/all", {
        ...data,
      });
      setQueries(response?.data?.data);
      setTotalRecords(response?.data?.meta?.filter_count);
    } catch (error) {
      // Failed to fetch candidate data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getQuery();
  }, [offset, rowsPerPage, searchValue, sortBy, selectedQueryStatus,  dateRange]);

  const handlePageChange = (event: any, newPage: any) => {
    setPage(newPage);
    setOffset(newPage * rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = useCallback(
    (val: string) => {
      if (val !== searchValue) {
        if (searchValue === "" && val !== "") {
          // Starting a search, remember current page
          preSearchPagination.current = { page, offset };
          setOffset(0);
          setPage(0);
        } else if (searchValue !== "" && val === "") {
          // Clearing a search, restore previous page
          setPage(preSearchPagination.current.page);
          setOffset(preSearchPagination.current.offset);
        } else {
          // Continuing search or refining, keep page 0
          setOffset(0);
          setPage(0);
        }
        setSearchValue(val);
      }
    },
    [searchValue, page, offset]
  );

  const handleQueryStatusFilterChange = (event: any, newValue: any) => {
    setSelectedQueryStatus(newValue);
  };

  
  
  const checkPermission = () => {
    const user: any = getUser();
    const module = ROLES_SLUGS?.queryTracker;
    const permission = PERMISSIONS_SLUGS?.edit;
    if (user?.roles?.admin_access === false) {
      if (user?.roles?.permisssions?.length > 0) {
        user?.roles?.permisssions?.forEach((item: any) => {
          if (item?.slug_name === module) {
            item?.actions?.forEach((permi: any) => {
              if (permi === permission) {
                setPermissionValid(true);
              }
            });
          }
        });
      }
    } else {
      setPermissionValid(true);
    }
  };

  useEffect(() => {
    checkPermission();
  }, []);

  const eligiblityComparionBreadCrumbs: BreadcrumbsModel[] = [
    { name: "Query Tracker", route: "/admin/query-tracker" },
    { name: "Eligibility Comparison" },
  ];

  return (
    <PageWrapper>
      <PageHeader title="Query Tracker" />
      <Grid2
        container
        sx={{
          marginTop: "2rem",
        }}
      >
        <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
          <Search label="Search" onSearch={handleSearch} value={searchValue} />
        </Grid2>
        <Grid2
          size={{ xs: 12, sm: 9, md: 9 }}
          sx={{ marginTop: { xs: "10px", sm: "0px" } }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "end" },
              flexWrap: "wrap",
            }}
            gap={2}
          >
            <DateRangePicker
                      startDate={startDate}
                      endDate={endDate}
                      onStartDateChange={setStartDate}
                      onEndDateChange={setEndDate}
                      onDateRangeChange={handleDateRangeChange}
                      endMaxDate={moment().toDate()}
                      startMaxDate={moment().toDate()}
                    />
            {/* Status Button */}
             <Autocomplete
                          size="small"
                          disablePortal
                          options={QUERY_STATUS || []}
                          getOptionLabel={(option: any) => option?.title || ""}
                          value={selectedQueryStatus} // Set selected status
                          onChange={(event, newValue) => {
                            handleQueryStatusFilterChange(event, newValue);
                            setOffset(0);
                            setPage(0);
                            preSearchPagination.current = { page: 0, offset: 0 };
                          }}
                          sx={{
                            width: { xs: "50%", sm: "150px", md: "150px" },
                            "& .MuiAutocomplete-inputRoot": {
                              backgroundColor: "white",
                              borderRadius: "10px",
                              marginTop:"2px"
                            },
                            "&:hover:": {
                              border: "transparent",
                            },
                            "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
                              "&:hover": {
                                backgroundColor: "transparent", // Remove hover effect
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Query Status"
                              variant="outlined"
                              placeholder="Query Status"
                              InputProps={{
                                ...params.InputProps,
                                // disableUnderline: true,
                                sx: {
                                  backgroundColor: "white",
                                  padding: "6px 12px",
                                  "&:hover": {
                                    borderColor: "#fff",
                                  },
                                },
                              }}
                            />
                          )}
                        />                       
          </Box>
        </Grid2>
      </Grid2>
      <Box
        marginTop={1}
        sx={{ overflowY: "auto", flex: "1 1 auto", display: "grid" }}
      >
        <TableContainer className="primary__table">
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Query Title</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy == "user_id.first_name"}
                    direction={sortOrder}
                    onClick={() => handleSort("user_id.first_name")}
                  >
                    Query Raised By
                  </TableSortLabel>{" "}
                </TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Position Title</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy == "date_created"}
                    direction={sortOrder}
                    onClick={() => handleSort("date_created")}
                  >
                    Date Raised
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Query Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ height: "450px" }}>
                      <DataLoader />{" "}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : queries?.length > 0 ? (
                queries?.map((query: any) => (
                  <TableRow key={query?.id}>
                    <TableCell>{query?.title}</TableCell>
                    <TableCell>
                      {combinedName(
                        query?.user_id?.first_name,
                        query?.user_id?.last_name
                      )}
                    </TableCell>
                    <TableCell>{query?.user_id?.email}</TableCell>
                    <TableCell>{query?.position_id?.name}</TableCell>
                    <TableCell align="center">
                    {handleDate(query?.date_created)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          permissionValid
                            ? query?.query_status === "open"
                              ? "Click here to change status"
                              : "You can't reopen the query"
                            : ""
                        }
                        arrow
                      >
                        <IconButton
                          sx={{
                            "&:hover": {
                              backgroundColor: "transparent", // Removes background color change
                              boxShadow: "none", // Removes shadow if any
                            },
                          }}
                          disableRipple
                        >
                          <Switch
                            onChange={(data) => {
                              setSwitchData(query);
                              handleSwitchClick();
                            }}
                            disabled={
                              query?.query_status === "closed" ||
                              !permissionValid
                            }
                            defaultChecked={
                              query?.query_status === "open" ? true : false
                            }
                            label={query?.query_status}
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <PermissionAction
                        module={ROLES_SLUGS?.queryTracker}
                        permission={PERMISSIONS_SLUGS?.read}
                      >
                        <Tooltip title="View" arrow>
                          <IconButton
                            size="small"
                            className="table__icons"
                            onClick={() =>
                              navigate("/admin/query-tracker/view", {
                                state: {
                                  data: query,
                                  queryStatusState: selectedQueryStatus,
                                  paginationState: { page, rowsPerPage, offset },
                                },
                              })
                            }
                          >
                            <img src={ViewIcons} className="view" />
                          </IconButton>
                        </Tooltip>
                      </PermissionAction>
                      <PermissionAction
                        module={ROLES_SLUGS?.queryTracker}
                        permission={PERMISSIONS_SLUGS?.edit}
                      >
                        <Tooltip title="Respond" arrow>
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/admin/query-tracker/respond/${query?.id}`,
                                {
                                  state: {
                                    data: query,
                                    queryStatusState: selectedQueryStatus,
                                    paginationState: { page, rowsPerPage, offset },
                                  },
                                }
                              )
                            }
                            size="small"
                            className="table__icons"
                            sx={{
                              opacity:
                                query?.query_status === "closed"
                                  ? "50%"
                                  : "100%",
                            }}
                            disabled={query?.query_status === "closed"}
                          >
                            <img src={RespondIcons} className="respond" />
                          </IconButton>
                        </Tooltip>
                      </PermissionAction>
                       <Tooltip title="Eligibility Comparison" arrow>
                              <IconButton
                                size="small"
                                className="table__icons"
                                sx={{ fontSize: "14px", color: "#4880FF" }}
                                onClick={() =>
                                  navigate(
                                    `eligibility-comparison/${query?.user_id?.id}/${query?.candidate_pos_eligibility_id}`,
                                    {
                                      state: {...query,breadCrumbs:eligiblityComparionBreadCrumbs},
                                    }
                                  )
                                }
                              >
                              <DifferenceIcon sx={{color:"black",opacity:'60%'}}/>
                              </IconButton>
                            </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box
                      sx={{
                        height: "450px",
                      }}
                    >
                      <DataNotFound />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {queries?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>
      </Box>
      {/* <ConfirmationDialog
        open={isSwitchDialogOpen}
        onClose={handleSwitchCancel}
        onConfirm={handleSwitchConfirm}
        summary={"Are you sure want to change the status?"}
        title={"Change Status"}
      /> */}
      <QueryChangeStatusDialog
        open={isSwitchDialogOpen}
        loading={switchLoading}
        onClose={handleSwitchCancel}
        onConfirm={(data) => {
          handleSwitchConfirm(data);
        }}
        resetForm={switchDialogReset}
      />
    </PageWrapper>
  );
};
export default QueryManagementListing;
